import { useEffect, useRef } from 'react';

import { isFaceDrawn } from '../domain/analysis';
import { useTranslate } from '../i18n/useI18n';

import type { OutputSettings } from '../domain/analysis';
import type { MediaRole } from '../domain/media';
import type { MessageKey } from '../i18n/messages';
import type { Translate } from '../i18n/types';
import type { FaceResult } from '../lib/faceAnalysis';
import type { ReactElement } from 'react';

const expressionLabelKeys: Readonly<Record<string, MessageKey>> = {
    neutral: 'results.expression.neutral',
    happy: 'results.expression.happy',
    sad: 'results.expression.sad',
    angry: 'results.expression.angry',
    fearful: 'results.expression.fearful',
    disgusted: 'results.expression.disgusted',
    surprised: 'results.expression.surprised',
};
const contourRanges: Array<[number, number, boolean]> = [
    [0, 16, false],
    [17, 21, false],
    [22, 26, false],
    [27, 30, false],
    [31, 35, false],
    [36, 41, true],
    [42, 47, true],
    [48, 59, true],
    [60, 67, true],
];

function getReferenceLabel(labels: string[], index: number, translate: Translate): string {
    const label = labels[index];
    return label !== undefined && label.length > 0
        ? label
        : translate('results.referenceNumber', { number: index + 1 });
}

function getTextLines(
    face: FaceResult,
    index: number,
    role: MediaRole,
    labels: string[],
    output: OutputSettings,
    translate: Translate
): string[] {
    const lines: string[] = [];
    if (output.drawLabels) {
        if (role === 'reference') lines.push(getReferenceLabel(labels, index, translate));
        else if (face.match?.isMatch === true && face.match.referenceIndex !== undefined) {
            lines.push(getReferenceLabel(labels, face.match.referenceIndex, translate));
            lines.push(`${translate('results.metric.distance')} ${face.match.distance.toFixed(3)}`);
        } else if (face.match !== undefined) {
            lines.push(
                translate('results.noMatch'),
                `${translate('results.metric.distance')} ${face.match.distance.toFixed(3)}`
            );
        } else lines.push(translate('results.faceNumber', { number: index + 1 }));
    }
    if (output.drawScores) {
        lines.push(
            `${translate('results.metric.detection')} %${String(Math.round(face.score * 100))}`
        );
    }
    if (face.age !== undefined && face.gender !== undefined && face.gender.length > 0) {
        const gender = translate(
            face.gender === 'female' ? 'results.gender.female' : 'results.gender.male'
        ).toLocaleLowerCase();
        lines.push(
            `≈${String(Math.round(face.age))} ${translate('results.metric.age')} · ${gender} %${String(Math.round((face.genderProbability ?? 0) * 100))}`
        );
    }
    if (face.expressions !== undefined) {
        Object.entries(face.expressions)
            .filter(([, probability]) => probability >= output.expressionMinProbability)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .forEach(([expression, probability]) =>
                lines.push(
                    `${expressionLabelKeys[expression] === undefined ? expression : translate(expressionLabelKeys[expression])} %${String(Math.round(probability * 100))}`
                )
            );
    }
    return lines;
}

function drawLabel(
    context: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    y: number,
    fontSize: number,
    color: string
): void {
    if (lines.length === 0) return;
    const padding = 6;
    const lineHeight = fontSize * 1.35;
    context.font = `600 ${String(fontSize)}px Manrope, sans-serif`;
    const width = Math.max(...lines.map((line) => context.measureText(line).width)) + padding * 2;
    const height = lineHeight * lines.length + padding * 2;
    const top = Math.max(0, y - height);
    context.fillStyle = 'rgba(4, 15, 13, 0.86)';
    context.fillRect(x, top, width, height);
    context.fillStyle = color;
    lines.forEach((line, index) => {
        context.fillText(line, x + padding, top + padding + fontSize + index * lineHeight);
    });
}

function drawLandmarks(
    context: CanvasRenderingContext2D,
    positions: Array<{ x: number; y: number }>,
    output: OutputSettings
): void {
    if (output.drawLandmarkLines && positions.length >= 68) {
        context.lineWidth = output.lineWidth;
        context.strokeStyle = output.landmarkLineColor;
        contourRanges.forEach(([start, end, closed]) => {
            const first = positions[start];
            if (first === undefined) return;
            context.beginPath();
            context.moveTo(first.x, first.y);
            for (let index = start + 1; index <= end; index += 1) {
                const point = positions[index];
                if (point !== undefined) context.lineTo(point.x, point.y);
            }
            if (closed) context.closePath();
            context.stroke();
        });
    }
    if (output.drawLandmarkPoints) {
        context.fillStyle = output.landmarkPointColor;
        positions.forEach((point) => {
            context.beginPath();
            context.arc(point.x, point.y, output.pointSize, 0, Math.PI * 2);
            context.fill();
        });
    }
}

export function ResultOverlay({
    media,
    faces,
    role,
    labels,
    output,
}: {
    media: HTMLImageElement | HTMLVideoElement | null;
    faces: FaceResult[];
    role: MediaRole;
    labels: string[];
    output: OutputSettings;
}): ReactElement {
    const translate = useTranslate();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas === null || media === null) return;
        const width = media instanceof HTMLVideoElement ? media.videoWidth : media.naturalWidth;
        const height = media instanceof HTMLVideoElement ? media.videoHeight : media.naturalHeight;
        if (width <= 0 || height <= 0) return;
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (context === null) return;
        context.clearRect(0, 0, width, height);
        faces.forEach((face, index) => {
            if (!isFaceDrawn(face, role, output)) return;
            const color =
                face.match !== undefined
                    ? face.match.isMatch
                        ? output.matchColor
                        : output.unknownColor
                    : output.boxColor;
            if (output.drawBoxes) {
                context.strokeStyle = color;
                context.lineWidth = output.lineWidth;
                context.strokeRect(face.box.x, face.box.y, face.box.width, face.box.height);
            }
            if (face.landmarks !== undefined) drawLandmarks(context, face.landmarks, output);
            drawLabel(
                context,
                getTextLines(face, index, role, labels, output, translate),
                Math.max(0, face.box.x),
                Math.max(0, face.box.y),
                output.fontSize,
                color
            );
        });
    }, [faces, labels, media, output, role, translate]);
    return (
        <canvas
            ref={canvasRef}
            className='result-overlay'
            aria-hidden='true'
        />
    );
}
